import { createBrowserRouter, Navigate } from 'react-router-dom';
import { LoginPage } from '@/features/auth/LoginPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { AcceptInvitePage } from '@/features/auth/AcceptInvitePage';
import { MainLayout } from '@/layouts/MainLayout';
import { PortalLayout } from '@/layouts/PortalLayout';
import { ProtectedRoute } from '@/app/ProtectedRoute';
import { MyDayPage } from '@/features/my-day/MyDayPage';
import { ClientsPage } from '@/features/clients/ClientsPage';
import { StaffPage } from '@/features/staff/StaffPage';
import { GestaoPage } from '@/features/gestao/GestaoPage';
import { AccountsPage } from '@/features/accounts/AccountsPage';
import { CategoriesPage } from '@/features/categories/CategoriesPage';
import { CostCentersPage } from '@/features/cost-centers/CostCentersPage';
import { CounterpartiesPage } from '@/features/counterparties/CounterpartiesPage';
import { PlaybooksPage } from '@/features/playbooks/PlaybooksPage';
import { PublicOnboardingFormPage } from '@/features/onboarding/PublicOnboardingFormPage';
import { AccountStatementPage } from '@/features/reports/AccountStatementPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { ReportView } from '@/features/reports/ReportView';
import { TransactionsPage } from '@/features/transactions/TransactionsPage';
import { ReconciliacaoPage } from '@/features/reconciliation/ReconciliacaoPage';
import { TasksPage } from '@/features/tasks/TasksPage';
import { CommercialPage } from '@/features/commercial/CommercialPage';
import { CashFlowPage } from '@/features/cashflow/CashFlowPage';
import { PortalDashboardPage } from '@/features/portal/PortalDashboardPage';
import { PortalTransactionsPage } from '@/features/portal/PortalTransactionsPage';
import { PortalApprovalsPage } from '@/features/portal/PortalApprovalsPage';
import { PortalReportsPage } from '@/features/portal/PortalReportsPage';
import { MessagesPage } from '@/features/messages/MessagesPage';
import { ImplementacoesPage } from '@/features/tickets/ImplementacoesPage';
import { DREPage } from '@/features/dre/DREPage';
import { ChannelResultPage } from '@/features/channel-result/ChannelResultPage';
import { DocumentsPage } from '@/features/documents/DocumentsPage';
import { TimelinePage } from '@/features/timeline/TimelinePage';
import { TreatmentPackagesPage } from '@/features/treatment-packages/TreatmentPackagesPage';
import { CardSplitsPage } from '@/features/card-splits/CardSplitsPage';
import { AgreementsPage } from '@/features/convenios/AgreementsPage';
import { BoletosPage } from '@/features/boletos/BoletosPage';
import { CurrentClientProvider } from '@/hooks/useCurrentClient';
import { FinanceProvider } from '@/contexts/FinanceContext';
import { STAFF_ROLES, MANAGER_ROLES } from '@/hooks/useRole';

function StaffAppShell() {
  return (
    <CurrentClientProvider>
      <FinanceProvider>
        <MainLayout />
      </FinanceProvider>
    </CurrentClientProvider>
  );
}

function PortalAppShell() {
  return (
    <CurrentClientProvider>
      <FinanceProvider>
        <PortalLayout />
      </FinanceProvider>
    </CurrentClientProvider>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/accept-invite', element: <AcceptInvitePage /> },

  {
    path: '/app',
    element: <ProtectedRoute allowedRoles={STAFF_ROLES} />,
    children: [
      {
        element: <StaffAppShell />,
        children: [
          { index: true, element: <Navigate to="/app/meu-dia" replace /> },
          { path: 'meu-dia', element: <MyDayPage /> },
          { path: 'clientes', element: <ClientsPage /> },
          { path: 'contas', element: <AccountsPage /> },
          { path: 'categorias', element: <CategoriesPage /> },
          { path: 'centros-de-custo', element: <CostCentersPage /> },
          { path: 'contrapartes', element: <CounterpartiesPage /> },
          { path: 'transacoes', element: <TransactionsPage /> },
          { path: 'conciliacao', element: <ReconciliacaoPage /> },
          { path: 'fluxo-de-caixa', element: <CashFlowPage /> },
          { path: 'dre', element: <DREPage /> },
          { path: 'resultado-por-canal', element: <ChannelResultPage /> },
          { path: 'documentos', element: <DocumentsPage /> },
          { path: 'timeline', element: <TimelinePage /> },
          { path: 'pacotes', element: <TreatmentPackagesPage /> },
          { path: 'cartoes', element: <CardSplitsPage /> },
          { path: 'convenios', element: <AgreementsPage /> },
          { path: 'boletos', element: <BoletosPage /> },
          { path: 'mensagens', element: <MessagesPage /> },
          { path: 'tarefas', element: <TasksPage /> },
          { path: 'relatorios', element: <ReportsPage /> },
          { path: 'relatorios/extrato', element: <AccountStatementPage /> },
          { path: 'relatorios/:kind', element: <ReportView /> },
          { path: 'comercial', element: <CommercialPage /> },
          {
            element: <ProtectedRoute allowedRoles={MANAGER_ROLES} />,
            children: [
              { path: 'colaboradores', element: <StaffPage /> },
              { path: 'gestao', element: <GestaoPage /> },
              { path: 'playbooks', element: <PlaybooksPage /> },
              { path: 'implementacoes', element: <ImplementacoesPage /> },
            ],
          },
        ],
      },
    ],
  },

  {
    path: '/portal',
    element: <ProtectedRoute allowedRoles={['cliente']} />,
    children: [
      {
        element: <PortalAppShell />,
        children: [
          { index: true, element: <Navigate to="/portal/dashboard" replace /> },
          { path: 'dashboard', element: <PortalDashboardPage /> },
          { path: 'transacoes', element: <PortalTransactionsPage /> },
          { path: 'dre', element: <DREPage /> },
          { path: 'documentos', element: <DocumentsPage /> },
          { path: 'timeline', element: <TimelinePage /> },
          { path: 'aprovacoes', element: <PortalApprovalsPage /> },
          { path: 'relatorios', element: <PortalReportsPage /> },
          { path: 'mensagens', element: <MessagesPage /> },
        ],
      },
    ],
  },

  { path: '/onboarding/:token', element: <PublicOnboardingFormPage /> },
  { path: '/', element: <Navigate to="/app" replace /> },
  { path: '*', element: <Navigate to="/" replace /> },
]);
